/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-non-null-assertion, no-multi-assign */
import { formantBotAvatarMintHex, type FormantBotAvatar } from '@cortex/types';

/** Canvas agent face from ~/Downloads/agent-avatars/avatar.html. One beam, 4s loop. */

export type AgentAvatarState = 'idle' | 'listening' | 'thinking' | 'done' | 'error';

const T = 4;
const TAU2 = Math.PI * 2;
const DEF = {
  mint: '#9DFFC8',
  lav: '#B9B4F8',
  amber: '#FFB547',
  border: '#3A3A39',
  tau: 1,
  lw: 1,
  bloom: 1,
  grat: 1,
  voice: 1,
};

const A = (k: number, t: number) => (TAU2 * k * t) / T;
const frac = (x: number) => ((x % 1) + 1) % 1;
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const sstep = (x: number) => {
  x = clamp(x, 0, 1);
  return x * x * x * (x * (x * 6 - 15) + 10);
};
const wrapT = (x: number) => {
  x = ((x % T) + T) % T;
  return x > T / 2 ? x - T : x;
};

const MARK_D = [
  'M33.6 0 H109.2 V21.9 H33.6 Z',
  'M0 100 L33.7 30.8 L67.4 100 Z',
  'M56.2 49.8 A17.3 17.3 0 1 1 90.8 49.8 A17.3 17.3 0 1 1 56.2 49.8 Z',
];

type Mark = { pts: number[][]; n: number };
let markCache: Mark | null = null;

function mark(): Mark {
  if (markCache) return markCache;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const pts: number[][] = [];
  const N = 600;
  const paths = MARK_D.map((d) => {
    const p = document.createElementNS(svg.namespaceURI, 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
    return p as SVGPathElement;
  });
  const lens = paths.map((p) => p.getTotalLength());
  const tot = lens.reduce((a, b) => a + b, 0);
  paths.forEach((p, si) => {
    const n = Math.round((N * (lens[si] ?? 0)) / tot);
    const len = lens[si] ?? 0;
    for (let i = 0; i < n; i++) {
      const q = p.getPointAtLength((len * i) / n);
      pts.push([((q.x - 54.6) / 54.6) * 0.88, ((q.y - 50) / 54.6) * 0.88, si]);
    }
  });
  markCache = { pts, n: pts.length };
  return markCache;
}

function markAt(u: number): [number, number, number] {
  const m = mark();
  const f = frac(u) * m.n;
  const i = Math.floor(f);
  const a = m.pts[i % m.n] ?? [0, 0, 0];
  const b = m.pts[(i + 1) % m.n] ?? a;
  const k = f - i;
  return [a[0]! + (b[0]! - a[0]!) * k, a[1]! + (b[1]! - a[1]!) * k, a[2] === b[2] ? 0 : 1];
}

const SYL: [number, number, number][] = [
  [0.25, 0.12, 0.7],
  [0.62, 0.1, 1],
  [1.05, 0.16, 0.85],
  [1.45, 0.09, 0.55],
  [1.95, 0.2, 1],
  [2.45, 0.11, 0.8],
  [2.8, 0.14, 0.6],
  [3.3, 0.2, 0.95],
];
const env = (t: number, delay = 0) => {
  let e = 0.05;
  for (const [c, w, h] of SYL) {
    const d = wrapT(t - delay - c);
    e += h * Math.exp(-(d * d) / (w * w));
  }
  return Math.min(1, e);
};

type Fig = (tr: number, tau: number, t: number, look?: FormantBotAvatar) => number[];
type StateDef = {
  f: number;
  tau: number;
  voice: boolean;
  boost?: number;
  amber?: boolean;
  neutral?: boolean;
  maxSeg?: number;
  fig: Fig;
  e?: (t: number, lag?: number) => number;
  tauAt?: (t: number) => number;
  gainAt?: (t: number) => number;
};

const doneE = (t: number, lag = 0) => {
  const s = (((t - lag) % T) + T) % T;
  return sstep((s - 0.15) / 1.75) * (1 - sstep((s - 3.2) / 0.7));
};

const STATES: Record<AgentAvatarState, StateDef> = {
  idle: {
    f: 6,
    tau: 0.22,
    voice: false,
    boost: 0.85,
    fig(_tr, tau, t, look) {
      const ratio = look?.ratio ?? 2;
      const spin = look?.spin ?? 1;
      const u = TAU2 * 6 * tau;
      const br = 0.68 + 0.08 * Math.sin(A(1, t) - 1.2);
      const ph = 0.35 + 0.4 * Math.sin(A(1, t));
      if (look?.figure === 'orbit') {
        return [br * Math.cos(u * spin), br * 0.82 * Math.sin(u * spin)];
      }
      if (look?.figure === 'rose') {
        const r = br * Math.cos(ratio * u);
        return [r * Math.cos(u * spin), r * Math.sin(u * spin)];
      }
      if (look?.figure === 'ribbon') {
        return [br * Math.sin(u * spin), br * 0.5 * Math.sin(ratio * u + ph)];
      }
      return [br * Math.sin(u * spin), br * 0.95 * Math.sin(ratio * u * spin + ph)];
    },
  },
  listening: {
    f: 5,
    tau: 0.1,
    voice: true,
    boost: 0.8,
    fig(tr, tau) {
      const x = -0.92 + 1.84 * frac(5 * tau);
      const e = env(tau, tr ? 0.38 : 0) * (tr ? 0.45 : 1);
      const c = tr
        ? Math.sin(TAU2 * 17.5 * tau) + 0.4 * Math.sin(TAU2 * 36 * tau + 1)
        : Math.sin(TAU2 * 22.5 * tau) +
          0.5 * Math.sin(TAU2 * 45 * tau + 0.8) +
          0.25 * Math.sin(TAU2 * 67.5 * tau + 2);
      return [x, (0.85 * e * c) / 1.3];
    },
  },
  thinking: {
    f: 6,
    tau: 0.14,
    voice: true,
    boost: 0.9,
    fig(tr, tau, t, look) {
      const spin = look?.spin ?? 1;
      const u = TAU2 * 6 * tau;
      if (!tr) {
        const ph = A(1, t);
        const th = A(1, t) * spin;
        const x = 0.82 * Math.sin((look?.ratio ?? 3) * u + ph);
        const y = 0.82 * Math.sin(2 * u);
        return [x * Math.cos(th) - y * Math.sin(th), x * Math.sin(th) + y * Math.cos(th)];
      }
      const r = 0.58 * Math.cos(3 * u);
      const th = -A(1, t) * spin;
      const x = r * Math.cos(u);
      const y = r * Math.sin(u);
      return [x * Math.cos(th) - y * Math.sin(th), x * Math.sin(th) + y * Math.cos(th)];
    },
  },
  done: {
    f: 6,
    tau: 0.2,
    voice: false,
    maxSeg: 0.07,
    neutral: true,
    e: doneE,
    fig(tr, tau, t) {
      const u = 6 * tau;
      const e = doneE(t, tr ? 0.3 : 0);
      const th = TAU2 * u;
      const sc = tr
        ? [Math.sin(th + 0.9) * 0.8, Math.sin(2 * th) * 0.7]
        : [0.8 * Math.sin(th), 0.8 * Math.sin(2 * th + 0.6 + 0.4 * Math.sin(A(1, t)))];
      const m = markAt(u);
      const k = sstep(e);
      return [sc[0]! + (m[0] - sc[0]!) * k, sc[1]! + (m[1] - sc[1]!) * k, m[2] * k];
    },
    tauAt(t) {
      return 0.2 + 0.22 * doneE(t);
    },
    gainAt(t) {
      const x = (t - 1.95) / 0.1;
      return 1 + 0.2 * Math.exp(-(x * x));
    },
  },
  error: {
    f: 3,
    tau: 0.42,
    voice: false,
    amber: true,
    boost: 3.6,
    fig(_tr, tau) {
      const s = 6 * tau;
      const w = Math.floor(s) + sstep(frac(s) * 2.2);
      const x = -0.9 + 1.8 * frac(w / 8);
      const q = Math.floor(8 * tau) / 8;
      const y = 0.5 * Math.sin(A(3, q)) + 0.2 * Math.sin(A(7, q) + 1);
      const gap = frac(tau / 1.0 + 0.1);
      return [x, y, gap > 0.55 && gap < 0.66 ? 1 : frac(w / 8) < 0.02 ? 1 : 0];
    },
    gainAt(t) {
      return 0.85 + 0.15 * Math.sin(A(1, t));
    },
  },
};

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const mixw = (c: number[], k: number) => c.map((v) => Math.round(v + (255 - v) * k));

export type AvatarLayers = {
  S: number;
  g: HTMLCanvasElement;
  t: HTMLCanvasElement;
  b: HTMLCanvasElement;
};

export function makeAvatarLayers(S: number): AvatarLayers {
  const mk = () => {
    const c = document.createElement('canvas');
    c.width = c.height = S;
    return c;
  };
  return { S, g: mk(), t: mk(), b: mk() };
}

function round(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.roundRect(x, y, w, h, r);
}

/** Draw one frame. `layers` is reused by the caller; it is resized when `S` changes. */
export function renderAgentAvatar(
  canvas: HTMLCanvasElement,
  state: AgentAvatarState,
  t: number,
  S: number,
  layers: AvatarLayers,
  look?: FormantBotAvatar,
) {
  const P = {
    ...DEF,
    mint: look ? formantBotAvatarMintHex[look.mint] : DEF.mint,
    bloom: look?.bloom ?? DEF.bloom,
    tau: look?.tau ?? DEF.tau,
    voice: look ? (look.voice ? 1 : 0) : DEF.voice,
  };
  if (look) t = (t + look.phase * T) % T;
  const st = STATES[state];
  if (layers.S !== S) {
    for (const c of [layers.g, layers.t, layers.b]) {
      c.width = c.height = S;
    }
    layers.S = S;
  }
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  const gx = layers.g.getContext('2d');
  const tx = layers.t.getContext('2d');
  if (!ctx || !gx || !tx) return;
  const m = S * 0.016;
  const R0 = S - 2 * m;
  const rad = S * 0.215;
  const cx = S / 2;
  const cy = S / 2;
  const R = S * 0.325;
  ctx.clearRect(0, 0, S, S);
  ctx.save();
  round(ctx, m, m, R0, R0, rad);
  ctx.clip();
  const gr = ctx.createRadialGradient(cx, cy, 0, cx, cy, S * 0.62);
  gr.addColorStop(0, '#171A18');
  gr.addColorStop(0.55, '#0C0D0C');
  gr.addColorStop(1, '#050605');
  ctx.fillStyle = gr;
  ctx.fillRect(0, 0, S, S);
  if (P.grat > 0 && S >= 96) {
    ctx.strokeStyle = `rgba(244,244,242,${0.045 * P.grat})`;
    ctx.lineWidth = Math.max(1, S / 256);
    ctx.beginPath();
    const d = (R * 2) / 4;
    for (let i = -2; i <= 2; i++) {
      ctx.moveTo(cx + i * d, cy - 2 * d);
      ctx.lineTo(cx + i * d, cy + 2 * d);
      ctx.moveTo(cx - 2 * d, cy + i * d);
      ctx.lineTo(cx + 2 * d, cy + i * d);
    }
    ctx.stroke();
    ctx.strokeStyle = `rgba(244,244,242,${0.09 * P.grat})`;
    ctx.beginPath();
    const tk = S * 0.012;
    for (let i = -10; i <= 10; i++) {
      ctx.moveTo(cx + (i * d) / 5, cy - tk);
      ctx.lineTo(cx + (i * d) / 5, cy + tk);
      ctx.moveTo(cx - tk, cy + (i * d) / 5);
      ctx.lineTo(cx + tk, cy + (i * d) / 5);
    }
    ctx.stroke();
  }
  gx.clearRect(0, 0, S, S);
  tx.clearRect(0, 0, S, S);
  gx.globalCompositeOperation = 'lighter';
  tx.globalCompositeOperation = 'lighter';
  const tau0 = (st.tauAt ? st.tauAt(t) : st.tau) * P.tau;
  const gain = st.gainAt ? st.gainAt(t) : 1;
  const W = Math.min(tau0 * 3.2, 1.6);
  const N = Math.round(clamp(W * 560, 160, 520));
  const lw = Math.max(S * 0.019 * P.lw, S <= 64 ? 2.1 : S <= 128 ? 2.4 : 1.5);
  const colors = [hex(st.amber ? P.amber : P.mint)];
  const traces = [0];
  if (st.voice && P.voice && S > 64) {
    colors.push(hex(P.lav));
    traces.push(1);
  }
  traces.forEach((tr, ti) => {
    const eh = st.neutral && st.e ? st.e(t) : 0;
    const base = colors[ti] ?? colors[0]!;
    const col =
      eh > 0 ? base.map((v, i) => Math.round(v + ([232, 236, 232][i]! - v) * 0.85 * eh)) : base;
    const hot = mixw(col, 0.5);
    const lwt = lw * (tr ? 0.7 : 1) * (st.neutral && S <= 64 ? 1 - 0.3 * eh : 1);
    const amp = tr ? 0.5 : 1;
    const pts: number[][] = [];
    for (let i = 0; i <= N; i++) {
      const tau = t - W + (W * i) / N;
      const p = st.fig(tr, tau, t, look);
      pts.push([cx + (p[0] ?? 0) * R, cy + (p[1] ?? 0) * R, p[2] || 0, tau]);
    }
    let mean = 0;
    for (let i = 0; i < N; i++) {
      mean += Math.hypot(
        (pts[i + 1]?.[0] ?? 0) - (pts[i]?.[0] ?? 0),
        (pts[i + 1]?.[1] ?? 0) - (pts[i]?.[1] ?? 0),
      );
    }
    mean = Math.max(mean / N, 0.05);
    for (let i = 0; i < N; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      if (!a || !b) continue;
      if (a[2]! > 0.5 || b[2]! > 0.5) continue;
      const len = Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!);
      if (len > S * (st.maxSeg || 0.4)) continue;
      const dw = clamp(mean / Math.max(len, 0.05), 0.4, 2.6);
      const dec = Math.exp(-(t - (a[3]! + b[3]!) / 2) / tau0);
      const I = ((dec * dw * 0.6 * gain * amp) / Math.max(1, tau0 * st.f * 0.9)) * (st.boost || 1);
      gx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${clamp(I * 0.45 * P.bloom, 0, 1)})`;
      gx.lineWidth = lwt * 2.6;
      gx.beginPath();
      gx.moveTo(a[0]!, a[1]!);
      gx.lineTo(b[0]!, b[1]!);
      gx.stroke();
      tx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${clamp(I, 0, 1)})`;
      tx.lineWidth = lwt;
      tx.beginPath();
      tx.moveTo(a[0]!, a[1]!);
      tx.lineTo(b[0]!, b[1]!);
      tx.stroke();
      tx.strokeStyle = `rgba(${hot[0]},${hot[1]},${hot[2]},${clamp(I * I * 0.9, 0, 1)})`;
      tx.lineWidth = lwt * 0.45;
      tx.stroke();
    }
  });
  ctx.globalCompositeOperation = 'lighter';
  const bx = layers.b.getContext('2d');
  if (!bx) {
    ctx.restore();
    return;
  }
  bx.clearRect(0, 0, S, S);
  bx.filter = `blur(${S * 0.045}px)`;
  bx.drawImage(layers.g, 0, 0);
  bx.filter = 'none';
  ctx.drawImage(layers.b, 0, 0);
  ctx.globalAlpha = 0.55;
  ctx.drawImage(layers.b, 0, 0);
  ctx.globalAlpha = 1;
  bx.clearRect(0, 0, S, S);
  bx.filter = `blur(${S * 0.012}px)`;
  bx.drawImage(layers.g, 0, 0);
  bx.filter = 'none';
  ctx.globalAlpha = 0.5;
  ctx.drawImage(layers.b, 0, 0);
  ctx.globalAlpha = 1;
  ctx.drawImage(layers.t, 0, 0);
  ctx.restore();
  ctx.globalCompositeOperation = 'source-over';
  ctx.strokeStyle = P.border;
  ctx.lineWidth = Math.max(S * 0.016, 1.5);
  round(
    ctx,
    m + ctx.lineWidth / 2,
    m + ctx.lineWidth / 2,
    R0 - ctx.lineWidth,
    R0 - ctx.lineWidth,
    rad - ctx.lineWidth / 2,
  );
  ctx.stroke();
}
